import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Grid, Box, BodySmall, FileSelectorFileType, CircularProgress, Button, SelectedFile } from '@parspec/pixel';

import { environment } from 'src/environments';
import { useQuoteContext } from '../../QuoteContext';
import { useQuoteAttachmentsQuery, useAttachmentDocumentsMutation } from '../../queries';
import ApplyToAllBomsButton from '../../../shared/ApplyToAllBomsButton';
import { UploadFileModal } from './uploadFileModalQuote';
import { BulkUploadFilesRequest } from 'src/features/Project/queries/apiTypes';
import { useUpdateAddFilesForProject } from 'src/features/Project/queries';
import { UploadedFilePath } from 'src/features/BOM/shared/types';
import { ATTACHMENTS_SECTION_NAME, ATTACHMENTS_SUB_SECTION_NAME } from '../../shared/constants';
import { SelectionChangedEvent } from 'ag-grid-community';
import { ProjectFileRow } from 'src/features/shared/ProjectsTable/types';

export interface IFileType {
    file: FileSelectorAndAPIDataFileType;
    s3_file_path?: string;
}

interface FileSelectorAndAPIDataFileType extends FileSelectorFileType {
    id?: number;
}

export interface IPreselectedFiles {
    id?: number | null;
    name: string;
    size: number;
    filepath: string;
    s3_url: string;
    size_in_byte?: number;
}

function Attachments() {
    const { projectId } = useParams();
    const { handlePdfPreview, isInitiateApiCalls, bomId, handleUpdateChangedSections } = useQuoteContext();
    const [openModal, setOpenModal] = useState(false)
    const [preSelectedFiles, setPreSelectedFiles] = useState<IPreselectedFiles[]>([]);
    const [uploadedFilePath, setUploadedFilePath] = useState<UploadedFilePath | null>(null);
    const [uploadedFile, setUploadedFile] = useState<IFileType[] | null>();

    const [fileRequiredError, setFileRequiredError] = useState('');
    const { data: attachmentData, isFetching: isAttachmentDataFetching } = useQuoteAttachmentsQuery(bomId, isInitiateApiCalls);
    const { mutateAsync: mutateDocuments, isLoading: isMutateDocumentsLoading } = useAttachmentDocumentsMutation();
    const { mutateAsync: addProjectFiles, isLoading: isUpdateProjectFileLoading } = useUpdateAddFilesForProject();

    useEffect(() => {
        if (isAttachmentDataFetching || !attachmentData?.data) {
            return;
        }

        let documentData = [];

        // adding the same s3 filepath in a new key "filepath" because in out fileUploader
        // the uploading logic is handled by the filepath key (refer line 66 in SelectedFiles.tsx in Pixel)
        // adding another key 'size' to make the key names uniform with those of the FileUploader
        if (attachmentData.data.documents.length > 0) {
            documentData = attachmentData.data.documents.map((el: any) => ({ ...el, filepath: el.s3_url, size: el.size_in_byte }));
        }
        setPreSelectedFiles(documentData);
    }, [isAttachmentDataFetching, attachmentData?.data]);
    const pushChangedFields = () => {
        handleUpdateChangedSections?.({ sectionName: ATTACHMENTS_SECTION_NAME, subSectionName: ATTACHMENTS_SUB_SECTION_NAME, value: true });
    };
    const onFileUploadedToS3 =
        async () => {
            try {
                if (!uploadedFilePath?.path) {
                    setFileRequiredError('Please select or upload a file first');
                    return;
                }
                const preFiles = preSelectedFiles.map((item) => {
                    return {
                        name: item.name,
                        s3_url: item.s3_url
                    }
                })
                const formatedDataFromFileSelector = uploadedFile && uploadedFile?.length > 0 ? uploadedFile?.map((item: any) => ({
                    name: item.file.name,
                    s3_url: item.s3_file_path.replace(environment.j, '')
                })) : [{
                    name: uploadedFilePath.name,
                    s3_url: uploadedFilePath.path.replace(environment.j, '')
                }];
                if (uploadedFile && uploadedFile.length > 0
                ) {
                    const newBulkUploadFilesRequest: BulkUploadFilesRequest[] = uploadedFile ? [{
                        filename: uploadedFile[0].file.name.substring(0, uploadedFile[0].file.name.lastIndexOf('.')),
                        filetype: uploadedFile[0].file.name.substring(uploadedFile[0].file.name.lastIndexOf('.') + 1),
                        file_size: uploadedFile[0].file.size || 0,
                        s3_url: environment.j + uploadedFile[0].s3_file_path
                    }] : [];

                    await mutateDocuments({ bomId, data: [...formatedDataFromFileSelector, ...preFiles] });
                    await addProjectFiles({ projectId: Number(projectId), payload: newBulkUploadFilesRequest || [] });
                    handleModalClose();
                    setUploadedFile(null)
                    handlePdfPreview(true);
                    pushChangedFields();
                } else {
                    await mutateDocuments({ bomId, data: [...formatedDataFromFileSelector, ...preFiles] });
                    handlePdfPreview(true);
                    pushChangedFields();
                    handleModalClose()
                }
            } catch (err) {

            }

        };

    const onUpload = (files: IFileType[]) => {
        if (files.length) {
            setUploadedFilePath?.({ name: files[0].file.name || '', path: files[0].s3_file_path || '' });
            setUploadedFile?.(files);
            setFileRequiredError?.('');
            return;
        }
        setFileRequiredError?.('');
        setUploadedFilePath?.(null);
        setUploadedFile?.(null);
    };

    const handleModalClose = () => {
        setUploadedFilePath(null)
        setOpenModal(false)
    }
    const handleResult = async () => {
        setPreSelectedFiles(attachmentData?.data.documents)
    }
    const onDelete = async (params: any) => {
        const filteredFiles = preSelectedFiles.filter((item: any) => item.id !== params.id);
        const result = filteredFiles.map((item: any) => ({
            name: item.name,
            s3_url: item.s3_url
        }));
        await mutateDocuments({ bomId, data: result });
        handlePdfPreview(true);
        pushChangedFields();
    }
    const onSelectionChanged = useCallback((param: SelectionChangedEvent<ProjectFileRow>) => {
        // Only files will be selected, get the first one(Ideally there'll be only one)
        const [selectedRow] = param.api.getSelectedRows();

        if (selectedRow) {
            setUploadedFilePath({
                name: selectedRow.file_name,
                path: selectedRow.s3_url,
                size: selectedRow.file_size
            })
        } else setUploadedFilePath(null);
        setFileRequiredError?.('')
    }, []);

    return (
        <Grid container direction={'column'} gap={2}>
            <Grid item position={'relative'} minWidth="350px">
                <Box m={2}>
                    <BodySmall limit={false} color="text.secondary">
                        Upload PDF documents in the sequence you want them to be attached to your quote.
                    </BodySmall>
                    <BodySmall limit={false} color="text.secondary">
                        For large files, preview might take longer to load.
                    </BodySmall>
                </Box>

                <Box mt={4} mb={2}>
                    <ApplyToAllBomsButton sectionName={ATTACHMENTS_SECTION_NAME} subSectionName={ATTACHMENTS_SUB_SECTION_NAME} />
                </Box>

                <Box m={4} ml={2} position={'relative'} width="500px">
                    {(isAttachmentDataFetching || isMutateDocumentsLoading) && (
                        <Box
                            position="absolute"
                            width="100%"
                            height="100%"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            sx={{ bgcolor: 'primary.contrastText', opacity: 0.8 }}
                            zIndex={200}
                        >
                            <Box width={1} height={1} display={'flex'} justifyContent={'center'} alignItems={'center'} zIndex={201}>
                                <CircularProgress color="primary" size={'lg'} />
                            </Box>
                        </Box>
                    )}
                    <Box mb={4}>

                        {preSelectedFiles && (
                            preSelectedFiles.map((item, index) => {
                                const itemData = {
                                    ...item,
                                    size: item.size_in_byte
                                }
                                return (
                                    <Box mt={1}>
                                        <SelectedFile file={itemData} onDelete={onDelete} handleResults={handleResult} url={`${environment.b}/api/generate_signed_url/`} index={index} isLoading={false} displayProgress={false} />
                                    </Box>
                                )
                            })
                        )}
                    </Box>
                    <Button color="secondary" variant="outlined" onClick={() => { setOpenModal(true) }}>
                        Add Attachment
                    </Button>
                </Box>
            </Grid>
            {openModal && (
                <UploadFileModal
                    isLoading={isUpdateProjectFileLoading || isMutateDocumentsLoading || isAttachmentDataFetching}
                    onUpload={onUpload}
                    open={openModal}
                    onCloseClick={handleModalClose}
                    onConfirm={onFileUploadedToS3}
                    fileRequiredError={fileRequiredError}
                    setUploadedFile={setUploadedFile}
                    setUploadedFilePath={setUploadedFilePath}
                    setFileRequiredError={setFileRequiredError}
                    onSelectionChanged={onSelectionChanged}
                />
            )}
        </Grid>
    );
}

export default Attachments;
